/* eslint-disable @typescript-eslint/naming-convention */
import { useContext, useRef, useState, useCallback } from "react";

// contexts
import { ApiContext, NEWRELIC_TABLES, recordNREvent, GroupOrderCartContext } from "@swiggy-private/checkout-core";
import { CartContext } from "@cart-client/contexts/CartContext";
import { CartToastContext } from "@cart-client/contexts/CartToastContext";
import { CartActionTypes } from "@cart-client/reducers/CartReducer";

// helpers
import {
    prepareNativeCartParamsFromReviewedCartResponse,
    isCartEmpty,
    clearMwebCartStorage,
} from "@cart-client/helpers/Cart";
import { useMountedRef, captureError, useEffectOnce } from "@swiggy-private/core-ui";

//apis
import { updateCartPublic } from "@cart-client/apis/publicCart";

// interfaces
import { UpdateCartData } from "@cart-client/interfaces/Checkout";
import { ApiResponse } from "@cart-client/interfaces/Api";
import { ReviewedCartApiResponse, NativeLocalCart, ApiMenuCartItem } from "@swiggy-private/cart-types";
import { isEmpty } from "@swiggy-private/js-utils";
import { logCartViewedEvent } from "@cart-client/helpers/Clevertap";
import { isClosingTimeAvailable } from "@cart-client/helpers/TimerOnCart";
import { TIMER_RESPONSE_SOURCE } from "@cart-client/constants/NewRelicEvents";
import { HTTP_STATUS_CODES } from "@cart-client/constants/index";
import { OOPS_SOMETHING_WENT_WRONG } from "@cart-client/constants/TextDict";
import { randomStringIdGenerator } from "@swiggy-private/core-ui";

// hooks
import { useLocalCartTransformer } from "@cart-client/hooks/useLocalCartTransformer";

interface UpdateCartState {
    updateCartInProgress: boolean;
    updateCartData?: UpdateCartData | null;
    updateCartError?: { httpStatusCode?: number };
}

type UseUpdateCartReturnType = [
    UpdateCartState,
    (
        data: NonNullable<UpdateCartState["updateCartData"]>,
    ) => Promise<ApiResponse<ReviewedCartApiResponse> | null | undefined>,
    VoidFunction,
];

// TODO: write logic and move to helpers
const getUpdateCartParams = (updateCartData: UpdateCartData): object => {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const newCartItems = updateCartData.cartItems?.map((cartItem) => {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const { valid_addons, valid_variants, valid_variants_v2, ...rest } = cartItem;
        if (!isEmpty(rest?.customer_request_messages)) {
            rest.customer_request_messages = rest?.customer_request_messages?.slice(0, rest.quantity) || [];
        }
        return rest;
    });

    updateCartData.cartItems = newCartItems as ApiMenuCartItem[];
    // eslint-disable-next-line @typescript-eslint/naming-convention
    return { ...updateCartData, cart_type: "REGULAR" };
};

export const usePerformCartUpdate = (): UseUpdateCartReturnType => {
    const isMountedRef = useMountedRef();
    const apiContextValue = useContext(ApiContext);
    const { setCartState, cartState } = useContext(CartContext);
    const { setCartToastData } = useContext(CartToastContext);
    const { isGroupCartFlow } = useContext(GroupOrderCartContext);
    const apiCallRef = useRef(false);
    const [updateCartState, setUpdateCartState] = useState<UpdateCartState>({ updateCartInProgress: false });

    const { prepareLocalCartForUpdateCartPayload } = useLocalCartTransformer();

    const apiHost = apiContextValue.host.unwrap();

    const handleUpdateCartSuccess = useCallback(
        async (response: ApiResponse<ReviewedCartApiResponse>, updateCartData?: UpdateCartData): Promise<void> => {
            if (response) {
                if (isCartEmpty(response.data)) {
                    setCartState({
                        type: CartActionTypes.CLEAR_LOCAL_CART,
                        payload: {
                            reviewedCart: response,
                        },
                    });
                    clearMwebCartStorage();
                } else {
                    // log clevertap event for cart viewed
                    logCartViewedEvent(response);
                    //TODO: Remove temporary new relic event added for cross-refrencing with analytics events after analysis
                    if (isClosingTimeAvailable(response)) {
                        recordNREvent({
                            eventType: NEWRELIC_TABLES.TIMER_RESPONSE_RECIEVED,
                            referer: TIMER_RESPONSE_SOURCE.PERFORM_UPDATE_CART,
                        });
                    }

                    const additional_context = cartState?.localCart?.cart_journey_ctx?.additional_context;
                    setCartState({
                        type: CartActionTypes.UPDATE_CART,
                        payload: {
                            uniqueIdForCart: randomStringIdGenerator(),
                            localCart:
                                (response.data &&
                                    prepareNativeCartParamsFromReviewedCartResponse(response.data, {
                                        orderComment: cartState?.localCart?.orderComment,
                                        cutleryComments: cartState?.localCart?.cutleryComments,
                                        cartLatLong: cartState?.localCart?.cartLatLong,
                                        sld: cartState?.localCart?.sld,
                                        userLiveLat: cartState?.localCart?.userLiveLat,
                                        userLiveLng: cartState?.localCart?.userLiveLng,
                                        // date will only come in case of bulk order
                                        date: cartState?.localCart?.date,
                                        is_membership_plan_removed: updateCartData?.is_membership_plan_removed,
                                        p_segment_id: cartState?.localCart?.p_segment_id,
                                        parent_restaurant_id: cartState?.localCart?.parent_restaurant_id,
                                        cart_initialization_source: cartState?.localCart?.cart_initialization_source,
                                        cart_category_type: cartState?.localCart?.cart_category_type,
                                        cart_journey_ctx: {
                                            cart_initialization_source:
                                                cartState?.localCart?.cart_journey_ctx?.cart_initialization_source,
                                            cart_category_type:
                                                cartState?.localCart?.cart_journey_ctx?.cart_category_type,
                                            request_contexts: cartState?.localCart?.cart_journey_ctx?.request_contexts,
                                            ...(additional_context && { additional_context }),
                                        },
                                        customer_selected_sla_option: updateCartData?.customer_selected_sla_option,
                                        flowContext: cartState?.localCart?.flowContext,
                                    })) ||
                                null,
                            reviewedCart: response,
                        },
                    });
                }
            }
        },
        [setCartState, cartState],
    );

    const performCartUpdate = useCallback(
        async (updateCartData: UpdateCartData): Promise<ApiResponse<ReviewedCartApiResponse> | null> => {
            if (!isMountedRef.current || apiCallRef.current || !updateCartData) {
                return null;
            }

            apiCallRef.current = true;
            updateCartData.created_from_group_cart = isGroupCartFlow;
            const updateCartParams = prepareLocalCartForUpdateCartPayload(getUpdateCartParams(updateCartData));

            try {
                const updateCartResponse = await updateCartPublic(apiHost, updateCartParams);

                // fetchData swallows HTTP errors (4xx/5xx) and returns {} (empty object).
                // Only intercept the HTTP error case: empty response with no statusCode.
                if (updateCartResponse?.statusCode === undefined && isEmpty(updateCartResponse?.data)) {
                    if (isMountedRef.current) {
                        apiCallRef.current = false;
                        setUpdateCartState({ updateCartInProgress: false });
                        setCartToastData({ message: OOPS_SOMETHING_WENT_WRONG, show: true });
                    }
                    return null;
                }

                await handleUpdateCartSuccess(updateCartResponse, updateCartData);
                if (isMountedRef.current) {
                    apiCallRef.current = false;
                    setUpdateCartState({ updateCartInProgress: false });
                }
                return updateCartResponse;
            } catch (e) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                const httpStatusCode = e?.status;
                let updateCartError;
                if (httpStatusCode == HTTP_STATUS_CODES.TOO_MANY_REQUESTS) {
                    updateCartError = {
                        httpStatusCode,
                    };
                }
                if (isMountedRef.current) {
                    apiCallRef.current = false;
                    setUpdateCartState({ updateCartData: null, updateCartInProgress: false, updateCartError });
                }
                captureError(e);
                return null;
            }
        },
        [
            isMountedRef,
            isGroupCartFlow,
            prepareLocalCartForUpdateCartPayload,
            apiHost,
            handleUpdateCartSuccess,
            setCartToastData,
        ],
    );

    const doUpdateCart = useCallback(
        async (updateCartData: UpdateCartData): Promise<ApiResponse<ReviewedCartApiResponse> | null | undefined> => {
            if (isMountedRef.current && !updateCartState.updateCartInProgress) {
                setUpdateCartState({ updateCartInProgress: true, updateCartData });
                return await performCartUpdate(updateCartData);
            }
        },
        [isMountedRef, updateCartState.updateCartInProgress, performCartUpdate],
    );

    const reloadCart = useCallback(async () => {
        if (isMountedRef.current && !updateCartState.updateCartInProgress) {
            setUpdateCartState({
                updateCartInProgress: true,
                updateCartData: { ...cartState.localCart, created_from_group_cart: isGroupCartFlow },
            });
            await performCartUpdate({
                ...(cartState.localCart as NativeLocalCart),
                created_from_group_cart: isGroupCartFlow,
            });
        }
    }, [isMountedRef, updateCartState.updateCartInProgress, cartState.localCart, isGroupCartFlow, performCartUpdate]);

    useEffectOnce(() => {
        // is mounted check..
        if (
            !isMountedRef.current ||
            apiCallRef.current ||
            !updateCartState.updateCartInProgress ||
            !updateCartState.updateCartData
        ) {
            return;
        }

        apiCallRef.current = true;
        const updateCartData = updateCartState.updateCartData;
        updateCartData.created_from_group_cart = isGroupCartFlow;
        const updateCartParams = prepareLocalCartForUpdateCartPayload(getUpdateCartParams(updateCartData));

        updateCartPublic(apiHost, updateCartParams)
            .then((response) => {
                // HTTP error: fetchData returns {} — no statusCode, no data
                if (response?.statusCode === undefined && isEmpty(response?.data)) {
                    if (isMountedRef.current) {
                        setCartToastData({ message: OOPS_SOMETHING_WENT_WRONG, show: true });
                    }
                    return;
                }
                return handleUpdateCartSuccess(response, updateCartData);
            })
            .catch(captureError)
            .then(() => {
                if (isMountedRef.current) {
                    apiCallRef.current = false;
                    setUpdateCartState({ updateCartData, updateCartInProgress: false });
                }
            });
    });

    return [updateCartState, doUpdateCart, reloadCart];
};
